import { type FC, useEffect, useState } from 'react';

import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import SelectableItems from '../../../components/selectable_items/selectable_items';
import { setActivePerson } from '../../../ducks/panels/asset_face_recognition/slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import type { SelectableItemsSelectionMode } from '../../../ui/selectable_items/types';
import { getStatus } from '../utils';
import ConfirmedPerson from './confirmed_person';
import RecognizedFaceContextMenu from './context_menu';
import { useOnPersonSegmentClick } from './hooks/use_on_person_segment_click';
import NewPerson from './new_person';
import UnconfirmedPerson from './unconfirmed_person';

import styles from './recognized_persons.module.css';

type Props = {
  persons: MlPersonByAssetAndVersionNullable[];
  personIdSegmentsMap: {
    [
      key: MlPersonByAssetAndVersionNullable['id']
    ]: AssetsSegmentElasticSchemaWithIdNullable[];
  };
};
const RecognizedPersons: FC<Props> = ({ persons, personIdSegmentsMap }) => {
  const [selectableItemIds, setSelectableItemIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] =
    useState<SelectableItemsSelectionMode>('common');
  const dispatch = useAppDispatch();
  const { store } = useBasePlayerContext();

  const handleSegmentClick = useOnPersonSegmentClick();
  const activePerson = useAppSelector(
    (state) => state.panelsState.assetFaceRecognition.activePerson,
  );
  const [newPersonIdInEditMode, setNewPersonIdInEditMode] = useState<string>();

  const handleOnPersonRowClick = (personId: string) => {
    if (selectionMode !== 'common') {
      // deactivate the active person and segment in the player when in multi-select mode
      store.segment.setSelectedSegmentIds([]);
      dispatch(setActivePerson(undefined));
      return;
    }

    const firstSegment = personIdSegmentsMap[personId]?.[0];
    if (firstSegment) {
      handleSegmentClick(firstSegment);
    }
  };

  useEffect(() => {
    if (activePerson?.id) {
      setSelectableItemIds([activePerson.id]);
    } else {
      setSelectableItemIds([]);
    }
  }, [activePerson?.id]);

  return (
    <div
      className={styles.root}
      role="complementary"
      aria-label="Recognized Persons"
    >
      <SelectableItems
        items={persons}
        idKey="id"
        selectedIds={selectableItemIds}
        onSelectIds={setSelectableItemIds}
        aria={{ label: 'Recognized persons items' }}
        contextMenu={RecognizedFaceContextMenu}
        onSelectionModeChange={setSelectionMode}
      >
        {persons.map((item) => {
          const status = getStatus(item);
          if (status === 'SYSTEM_CONFIRMED' || status === 'USER_CONFIRMED') {
            return (
              <ConfirmedPerson
                segments={personIdSegmentsMap[item.id]}
                person={item}
                key={item.id}
                highlighted={selectableItemIds.includes(item.id)}
                onPersonRowClick={handleOnPersonRowClick}
                setSelectableItemIds={setSelectableItemIds}
              />
            );
          }
          if (status === 'UNCONFIRMED') {
            return (
              <UnconfirmedPerson
                segments={personIdSegmentsMap[item.id]}
                person={item}
                key={item.id}
                highlighted={selectableItemIds.includes(item.id)}
                onPersonRowClick={handleOnPersonRowClick}
                setSelectableItemIds={setSelectableItemIds}
              />
            );
          }
          return (
            <NewPerson
              segments={personIdSegmentsMap[item.id]}
              person={item}
              key={item.id}
              highlighted={selectableItemIds.includes(item.id)}
              onPersonRowClick={handleOnPersonRowClick}
              setSelectableItemIds={setSelectableItemIds}
              newPersonIdInEditMode={newPersonIdInEditMode}
              setNewPersonIdInEditMode={setNewPersonIdInEditMode}
            />
          );
        })}
      </SelectableItems>
    </div>
  );
};

export default RecognizedPersons;
